<template>
  <div class="paywall">
    <div v-if="state" class="paywall__overlay">
      <slot name="content">
        <Card v-if="!hideContent" class="paywall__modules" theme="border" style="--card-spacing: 1rem 1.5rem">
          <template #header>
            <div class="row center middle">
              <FontAwesomeIcon :icon="['fal', 'layer-plus']" size="lg" class="mr-3" />
              <div>
                <div class="paywall__title">{{ $t('paywallContentFieldUsedByFollowingModules ') }}</div>
                <div class="paywall__readmore">
                  {{ $tc('paywallContentPressButtonToReadMore', availableItems.length) }}
                  <FontAwesomeIcon :icon="['fal', 'angle-down']" style="margin-left: 3px" />
                </div>
              </div>
            </div>
          </template>
          <template #default>
            <ButtonComponent
              v-for="item in availableItems"
              class="paywall__module text--small"
              :key="item.id"
              :label="item.name"
              @click="current = item"
              :icon="['fad', item.icon]"
            />
          </template>
        </Card>

        <Portal>
          <Modal
            v-if="current"
            :show="Boolean(current)"
            @show:update="current = null"
            @close="current = null"
            clickaway
          >
            <Pricing :value="current.id" :options="options" theme="flat">
              <template #actions>
                <ButtonComponent :label="$t('cancel')" :icon="['fal', 'times']" @click="current = null" />
                <ButtonComponent
                  :label="$t('activate')"
                  :icon="['fal', 'plus']"
                  theme="primary"
                  :labels="{ confirm: $t('confirm') }"
                  :icons="{ confirm: ['fas', 'question-circle'] }"
                  confirm
                  @click="submit(current.id)"
                  :promise="promise"
                />
              </template>
            </Pricing>
          </Modal>
        </Portal>
      </slot>
    </div>
    <slot />
  </div>
</template>

<script>
import { ToArray } from '@/utils'
import { ModalComponent as Modal } from 'vue-elder-modal'
import Card from '@/components/Card'
import Products from '@/components/Pricing/product-meta'
import Pricing from '@/components/Pricing'
import Portal from '@/components/Portal'

export default {
  props: {
    value: [Array, String],
    items: [Array, String],
    strategy: {
      type: String,
      default: 'OR',
      enum: ['OR', 'AND'],
    },
    action: Function,
    disabled: Boolean,
    hideContent: Boolean,
    options: {
      type: Object,
      default: () => ({}),
    },
  },
  watch: {
    value: {
      handler: 'reset',
      immediate: true,
    },
  },
  data() {
    return {
      current: null,
      promise: null,
    }
  },
  computed: {
    itemsComp() {
      return ToArray(this.items)
    },
    state() {
      if (this.disabled) return

      switch (this.strategy) {
        case 'AND':
          return !ToArray(this.value).every(v => this.itemsComp.includes(v))
        case 'OR':
        default:
          return !ToArray(this.value).some(v => this.itemsComp.includes(v))
      }
    },
    availableItems() {
      const meta = this.$store.state.enums.ProductMeta
      return Object.entries(meta)
        .map(([key, value]) => ({ id: key, ...value }))
        .filter(p => this.itemsComp.includes(p.id) && !ToArray(this.value).includes(p.id))
    },
  },
  methods: {
    submit(id) {
      this.promise = this.action(id)
    },
    reset() {
      this.current = null
      this.promise = null
    },
  },
  components: {
    Modal,
    Card,
    Pricing,
    Portal,
  },
}
</script>

<style lang="scss">
.paywall {
  position: relative;

  &__overlay {
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    z-index: 3;
    background-color: rgba(white, 0.85);

    padding: 2rem 1rem;

    display: flex;
    justify-content: center;
    align-items: flex-start;
  }

  &__title {
    font-weight: bold;
  }

  &__modules {
    position: sticky;
    top: 200px;

    & > .card__content {
      display: flex;
      flex-wrap: wrap;
      gap: 0.5rem;
      justify-content: center;
      align-items: center;
    }
  }

  &__readmore {
    font-size: 0.8em;
    opacity: 0.7;
  }

  .card__header {
    min-height: initial;
  }

  .card__title {
    margin-bottom: 0;
  }
}
</style>
